import { AsyncPipe, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { InsertionPlans, IsoDate, PlanView } from 'src/app/admin/pages/plan-management/data/plan-management.models';
import { PlanNameCatalogService } from 'src/app/admin/pages/plan-management/data/plan-name-catalog.service';
import { NameCatalog, parseIsoDate, regionName } from 'src/app/admin/pages/plan-management/domain/plan-presentation';
import { PlanChoiceListComponent, nameCatalog } from './plan-choice-list.component';

export interface PlanChoiceDialogData {
  assetId: string;
  day: IsoDate;
  /** The regions where several open plans hold the day. */
  answers: InsertionPlans[];
}

/**
 * Asked right before inspections are placed on a day two or more open plans hold, when no
 * plan was chosen for it in the Plan Assistant. It closes with one plan per region, or with
 * nothing when the user cancels, and then nothing is inserted.
 */
@Component({
  selector: 'phdsoft-plan-choice-dialog',
  standalone: true,
  imports: [AsyncPipe, DatePipe, MatButtonModule, MatDialogModule, PlanChoiceListComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @let names = catalog$ | async;
    <h2 mat-dialog-title>Choose the plan</h2>
    <mat-dialog-content>
      <p>
        {{ day | date: 'mediumDate' }} is in more than one open plan. The inspections you insert go to the plan
        you choose.
      </p>
      @for (answer of data.answers; track answer.RegionId) {
        @if (data.answers.length > 1) {
          <h3 class="plan-choice-dialog__region">{{ regionLabel(answer.RegionId, names) }}</h3>
        }
        <phdsoft-plan-choice-list
          [plans]="answer.Plans"
          [catalog]="names"
          [suggestedPlanId]="answer.SuggestedPlanId"
          [selectedPlanId]="selected.get(answer.RegionId)?.PlanId"
          (picked)="select(answer.RegionId, $event)"
        />
      }
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button type="button" mat-button mat-dialog-close>Cancel</button>
      <button type="button" mat-flat-button color="primary" (click)="insert()">Insert here</button>
    </mat-dialog-actions>
  `,
  styles: [`.plan-choice-dialog__region { margin: 1rem 0 0.5rem; font-size: 0.95rem; }`]
})
export class PlanChoiceDialogComponent {
  readonly data = inject<PlanChoiceDialogData>(MAT_DIALOG_DATA);
  private readonly ref = inject<MatDialogRef<PlanChoiceDialogComponent, PlanView[]>>(MatDialogRef);

  readonly day = parseIsoDate(this.data.day);
  readonly catalog$ = nameCatalog(
    inject(PlanNameCatalogService),
    this.data.assetId,
    this.data.answers.flatMap(answer => answer.Plans)
  );

  /** One plan per region, the suggested one until the user picks another. */
  readonly selected = new Map<string, PlanView>(
    this.data.answers.map(answer => [
      answer.RegionId,
      answer.Plans.find(plan => plan.PlanId === answer.SuggestedPlanId) ?? answer.Plans[0]
    ])
  );

  select(regionId: string, plan: PlanView): void {
    this.selected.set(regionId, plan);
  }

  regionLabel(regionId: string, catalog: NameCatalog | null): string {
    return catalog ? regionName(regionId, catalog) : '';
  }

  insert(): void {
    this.ref.close([...this.selected.values()]);
  }
}
